import { notify } from '../../../app';
import { getTelephony } from '../../../api';
import { getOutboundNumbers } from '../../../api';
import { addOutboundPhoneNumber } from '../../../api';
import { useFetch, useRequest } from '../../../hooks';
import { deleteOutboundPhoneNumber } from '../../../api';
import { updateTelephony as updateTelephonyApi } from '../../../api';
import { getSipProvidersList } from '../../../api';
import type { TelephonyResultType, SipProviderDto } from '../../../api';
import type { OutboundPhoneNumberType } from '../../../api';

export type AgentTelephonyFormType = TelephonyResultType & {
  outboundPhones: OutboundPhoneNumberType[];
};

export type PhoneType = OutboundPhoneNumberType;

export const useTelephony = () => {
  const { intializing: loadingTelephony, data: credentials } =
    useFetch(async () => {
      try {
        return await getTelephony();
      } catch {
        return {};
      }
    }, []);
  const [updateTelephony, updatingTelephony] = useRequest(
    (_signal, { outboundPhones, ...form }: AgentTelephonyFormType) =>
      updateTelephonyApi(form),
    {
      deps: [],
      onSuccess: () => notify.success('Updated credentials'),
      onError: () => notify.error('Failed to update credentials'),
    }
  );

  const {
    intializing: loadingOutbound,
    data: outboundPhones,
    refresh: refreshOutbound,
  } = useFetch(() => getOutboundNumbers(), []);

  const { data: sipProviders } = useFetch(async () => {
    const providers = await getSipProvidersList();
    return (providers as unknown as SipProviderDto[]).map(p => ({
      id: p.id,
      name: p.name,
    }));
  }, []);

  const loading = loadingOutbound || loadingTelephony;

  const form: AgentTelephonyFormType = {
    ...(credentials as TelephonyResultType),
    outboundPhones:
      outboundPhones?.map(ph => ({
        phoneNumber: ph.phoneNumber || '',
        id: ph.id,
      })) || [],
  };

  const [addOutboundPhonenumber, addingOutbound] = useRequest(
    async (_signal, phoneNumber: string) => {
      const res = await addOutboundPhoneNumber({ phoneNumber });
      return res.id;
    },
    {
      deps: [],
      onSuccess: () => {
        notify.success('Added phone number');
        refreshOutbound?.();
      },
      onError: () => notify.error('Failed to add phone number'),
    }
  );

  const [deleteOutboundPhonenumber, deletingOutbound] = useRequest(
    (_signal, phone: PhoneType) => deleteOutboundPhoneNumber(phone),
    {
      deps: [],
      onSuccess: () => {
        notify.success('Deleted phone number');
        refreshOutbound?.();
      },
      onError: () => notify.error('Failed to delete phone number'),
    }
  );

  const updating = addingOutbound || updatingTelephony || deletingOutbound;

  return {
    form,
    loading,
    updating,
    sipProviders: sipProviders || [],

    updateTelephony,

    addOutboundPhonenumber,

    deleteOutboundPhonenumber,
  };
};
